import { ChatLayoutContext } from '@/components/context/ChatLayoutContext';
import { ConversationList } from '@/components/chat/ConversationList';
import { DRAWER_WIDTH } from '@/constants/chat';
import { DrawerButton, DrawerIcon } from '@/components/chat/DrawerComponents';
import { getCurrentThread, setCurrentThread } from '@/store/reducers/UIState';
import { getOrderedThreads } from '@/store/reducers/ChatStore';
import { LoginButton } from '@/components/user/LoginButton';
import { LoginPromo } from '@/components/user/LoginPromo';
import {
  mkCollapseBtnTrackAttrs,
  mkExpandBtnTrackAttrs,
  mkNewThreadBtnTrackAttrs,
  mkViewThreadsBtnTrackAttrs,
} from '@/analytics/props/threadBar';
import { Thread } from '@/models/Thread';
import { ToS } from '@/components/chat/ToS';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useAuth0 } from '@/auth/Auth0Client';
import { useNavigateToChatPage } from '@/components/shared';
import { ZIndexDrawer } from '@/constants/zIndex';

import {
  alpha,
  Button,
  CSSObject,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  styled,
  Theme,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import GestureIcon from '@mui/icons-material/Gesture';
import MenuIcon from '@mui/icons-material/Menu';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const DEBUG_MODE_KEY = 'debugMode';

export const ChatDrawer = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isChatDrawerOpen: isOpen, setIsChatDrawerOpen } =
    useContext(ChatLayoutContext);
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const conversations: Thread[] = useAppSelector(getOrderedThreads);
  const currentThread: Thread | null = useAppSelector(getCurrentThread);
  const navigateToChatPage = useNavigateToChatPage();
  const { isAuthenticated } = useAuth0();
  const [debugMode, setDebugMode] = useState<boolean>(
    localStorage.getItem(DEBUG_MODE_KEY) === 'true',
  );

  const startNewThread = useCallback(() => {
    dispatch(setCurrentThread(null));
    navigateToChatPage(null);
  }, [dispatch, location.pathname, navigateToChatPage]);

  const disableDebugMode = () => {
    localStorage.setItem(DEBUG_MODE_KEY, 'false');
    setDebugMode(false);
  };

  const openDrawer = () => setIsChatDrawerOpen(true);
  const closeDrawer = () => setIsChatDrawerOpen(false);

  // Open the drawer by default on large screens
  useEffect(() => {
    if (!isSmallScreen) {
      setIsChatDrawerOpen(true);
    }
  }, []);

  const startNewThreadButton = (
    <ListItem disablePadding>
      <StartConvoButton
        isOpen={isOpen}
        onClick={startNewThread}
        {...mkNewThreadBtnTrackAttrs()}
      >
        <DrawerIcon $isOpen={isOpen}>
          <AddBoxOutlinedIcon />
        </DrawerIcon>
        <ListItemText
          primary="Start a new search"
          sx={{ opacity: isOpen ? 1 : 0 }}
        />
      </StartConvoButton>
    </ListItem>
  );

  return (
    <StyledDrawer variant="permanent" open={isOpen} anchor="left">
      <DrawerHeader>
        <ListItem disablePadding>
          <MenuButton
            isOpen={isOpen}
            onClick={isOpen ? closeDrawer : openDrawer}
            {...(isOpen ? mkCollapseBtnTrackAttrs() : mkExpandBtnTrackAttrs())}
          >
            <MenuDrawerIcon sx={{ display: 'none' }} />
            {isOpen && <CloseDrawerIcon />}
          </MenuButton>
        </ListItem>
        <Divider sx={{ borderColor: 'rgba(250,242,233,0.1)' }} />

        {currentThread &&
          (isOpen ? (
            startNewThreadButton
          ) : (
            <Tooltip title="Start a new search" arrow placement="right">
              {startNewThreadButton}
            </Tooltip>
          ))}

        {!isOpen && (
          <Tooltip title="Threads" arrow placement="right">
            <ThreadsButton
              $isOpen={isOpen}
              onClick={openDrawer}
              {...mkViewThreadsBtnTrackAttrs()}
            >
              <DrawerIcon $isOpen={isOpen}>
                <GestureIcon />
              </DrawerIcon>
            </ThreadsButton>
          </Tooltip>
        )}

        {isOpen && (
          <Fragment>
            {!isAuthenticated && <LoginPromo />}
            <ConversationsLabel>Recent Searches</ConversationsLabel>
          </Fragment>
        )}
      </DrawerHeader>

      {isOpen && (
        <ConversationList
          conversations={conversations}
          selectedConversationId={currentThread?.id}
        />
      )}

      <DrawerFooter>
        {debugMode && (
          <ListItem disablePadding>
            <Tooltip
              title="Debug Mode is ON, click to turn it OFF"
              arrow
              placement="right"
            >
              <DrawerButton $isOpen={isOpen} onClick={disableDebugMode}>
                <DrawerIcon $isOpen={isOpen}>
                  <VisibilityOffIcon style={{ color: 'orange' }} />
                </DrawerIcon>
                <ListItemText
                  primary="In Debug Mode"
                  sx={{ opacity: isOpen ? 1 : 0, color: 'orange' }}
                />
              </DrawerButton>
            </Tooltip>
          </ListItem>
        )}
        {(isAuthenticated || !isOpen) && (
          <ListItem disablePadding>
            <LoginButton displayIconOnly={!isOpen} />
          </ListItem>
        )}
        {isOpen && (
          <Fragment>
            <Divider />
            <ToS />
          </Fragment>
        )}
      </DrawerFooter>
    </StyledDrawer>
  );
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: '100%',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.up('sm')]: {
    width: DRAWER_WIDTH,
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  justifyContent: 'space-between',
  width: 0,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const StyledDrawer = styled(
  Drawer,
  {},
)(({ theme, open }) => ({
  boxSizing: 'border-box',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  width: DRAWER_WIDTH,

  '&.MuiDrawer-root': {
    position: 'relative',
    zIndex: ZIndexDrawer.chatDrawer,
  },

  '& .MuiPaper-root': {
    backgroundColor: theme.color['dark-teal-100'].hex,
    border: 'none',
  },

  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const DrawerHeader = styled(List)(({ theme }) => ({
  backgroundColor: theme.color['dark-teal-100'].hex,
  left: 0,
  padding: 0,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

export const DrawerFooter = styled(List)(({ theme }) => ({
  backgroundColor: theme.color['dark-teal-100'].hex,
  bottom: 0,
  left: 0,
  padding: theme.spacing(1, 0),
  position: 'sticky',
}));

const MenuDrawerIcon = styled(MenuIcon)`
  display: none;
  @media only screen and (min-width: 600px) {
    display: block;
  }
`;

const CloseDrawerIcon = styled(CloseIcon)`
  @media only screen and (min-width: 600px) {
    display: none;
  }
`;

const MenuButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  margin-left: auto;
  padding: ${({ theme }) => theme.spacing(2.25, 1.5)};

  & .MuiSvgIcon-root {
    color: ${({ theme }) => theme.palette.secondary.main};
  }

  /* Hide the ripple element */
  & .MuiTouchRipple-root {
    display: none;
  }

  @media only screen and (min-width: 600px) {
    margin-left: initial;
  }
`;

const StartConvoButton = styled(DrawerButton, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  backgroundColor: isOpen ? alpha(theme.color['teal-100'].hex, 0.2) : 'inherit',
  borderRadius: '4px',
  color: theme.color['cream-100'].hex,
  margin: theme.spacing(1.5, 1.5, 0, 1.5),

  '& .MuiListItemIcon-root': {
    color: theme.palette.secondary.main,
  },
}));

const ThreadsButton = styled(DrawerButton)(({ theme }) => ({
  '& .MuiListItemIcon-root': {
    color: theme.palette.secondary.main,
  },
}));

const ConversationsLabel = styled(ListItem)(({ theme }) => ({
  color: theme.color['cream-100'].hex,
  fontWeight: 700,
  padding: theme.spacing(2, 2, 0, 2),
}));
